import { type RescheduledFrequency, type SearchFrequencyViewModel } from '@generated';
import { ArrowRight, X } from 'lucide-react';
import { Button } from '~/shared/components/buttons/Button';
import { DateTimeFormField } from '~/shared/forms/controls/fields/date-fields/DateTimeFormField';
import { DisciplineChip } from '~/libraries/disciplines/DisciplineChip';
import { formatToShortDateAndTime } from '~/util/date';
import { getCadenceConstraintMessage } from './util';

export function RescheduledFrequenciesList({
	frequencies,
	rescheduled,
	onRemove
}: {
	frequencies: SearchFrequencyViewModel[];
	rescheduled: RescheduledFrequency[];
	onRemove: (frequencyId: string) => void;
}) {
	if (rescheduled.length === 0) {
		return (
			<p className="text-sm text-gray-500">
				Drag a weekly or monthly frequency to a new day, or click a frequency to change its time. Rescheduled frequencies will be listed here.
			</p>
		);
	}

	return (
		<div className="space-y-2">
			<span className="font-medium text-gray-900">Rescheduled Frequencies ({rescheduled.length})</span>
			{rescheduled.map((x, index) => {
				const frequency = frequencies.find(y => y.id === x.frequencyId)!;
				return (
					<div key={x.frequencyId} className="flex items-start justify-between gap-2 rounded border border-gray-300 bg-gray-50 p-2">
						<div className="flex flex-1 flex-col gap-2">
							<div className="flex items-center gap-2">
								<DisciplineChip disciplineId={frequency.disciplineId} label={frequency.frequencyLabel} />
								<span className="text-xs text-gray-500">{frequency.frequencyCadenceTypeLabel}</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="text-sm text-gray-600">{formatToShortDateAndTime(frequency.scheduledDateAndTime)}</span>
								<ArrowRight className="size-4 shrink-0 text-gray-400" />
								<DateTimeFormField
									name={`frequencies.${index}.scheduledDateAndTime`}
									granularity="minute"
									label="Scheduled Date/Time"
									hideLabel
								/>
							</div>
							<span className="text-xs text-gray-500">{getCadenceConstraintMessage(frequency.frequencyCadenceTypeId)}</span>
						</div>
						<Button Icon={X} appearance="outline" intent="destructive" onClick={() => onRemove(x.frequencyId)} aria-label="Undo Reschedule" />
					</div>
				);
			})}
		</div>
	);
}
